'use client';

import { getFacebookAppId } from './config';

import type { IFacebookLoginStatusResponse } from '@/types/facebook-sdk';

const FB_SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';
const FB_SDK_VERSION = 'v21.0';
const FB_SDK_LOAD_TIMEOUT_MS = 15000;

let sdkLoadPromise: Promise<void> | null = null;
let sdkInitialized = false;

function getFbOrThrow(): NonNullable<typeof window.FB> {
    if (!window.FB) {
        throw new Error('facebook_sdk_not_ready');
    }
    return window.FB;
}

export function isFacebookSdkReady(): boolean {
    return typeof window !== 'undefined' && !!window.FB && sdkInitialized;
}

/**
 * 加载并初始化 Facebook JS SDK（单例）。
 */
export function loadFacebookSdk(appId?: string): Promise<void> {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('facebook_sdk_ssr'));
    }

    const resolvedAppId = appId ?? getFacebookAppId();

    if (sdkLoadPromise) {
        return sdkLoadPromise;
    }

    sdkLoadPromise = new Promise<void>((resolve, reject) => {
        const initSdk = () => {
            try {
                getFbOrThrow().init({
                    appId: resolvedAppId,
                    cookie: true,
                    xfbml: false,
                    version: FB_SDK_VERSION,
                });
                sdkInitialized = true;
                resolve();
            } catch (err) {
                sdkLoadPromise = null;
                sdkInitialized = false;
                reject(err);
            }
        };

        if (window.FB) {
            initSdk();
            return;
        }

        const previousFbAsyncInit = window.fbAsyncInit;
        window.fbAsyncInit = () => {
            previousFbAsyncInit?.();
            initSdk();
        };

        const existing = document.querySelector(`script[src="${FB_SDK_URL}"]`);
        if (existing) {
            const startedAt = Date.now();
            const timer = window.setInterval(() => {
                if (window.FB) {
                    window.clearInterval(timer);
                    initSdk();
                } else if (Date.now() - startedAt > FB_SDK_LOAD_TIMEOUT_MS) {
                    window.clearInterval(timer);
                    sdkLoadPromise = null;
                    reject(new Error('facebook_sdk_load_failed'));
                }
            }, 100);
            return;
        }

        const script = document.createElement('script');
        script.src = FB_SDK_URL;
        script.async = true;
        script.defer = true;
        script.crossOrigin = 'anonymous';
        script.onerror = () => {
            sdkLoadPromise = null;
            reject(new Error('facebook_sdk_load_failed'));
        };
        document.body.appendChild(script);
    });

    return sdkLoadPromise;
}

/** 登录页 / 弹窗挂载时预加载，避免点击后再 async 加载导致桌面端 FB.login 无响应 */
export function preloadFacebookSdk(): void {
    loadFacebookSdk().catch(() => {
        // 预加载失败不打扰用户，点击时会走 redirect 或再试
    });
}

/**
 * 通过 FB.login 获取用户 access_token（须在用户点击同步栈内调用，桌面端适用）。
 */
export function loginWithFacebookForAccessToken(): Promise<string> {
    return new Promise((resolve, reject) => {
        getFbOrThrow().login(
            (response: IFacebookLoginStatusResponse) => {
                const token = response.authResponse?.accessToken;
                if (token) {
                    resolve(token);
                    return;
                }
                reject(new Error('facebook_login_cancelled'));
            },
            { scope: 'public_profile,email' }
        );
    });
}
